import { Box, CircularProgress, Typography } from '@mui/material';
import { FC, useEffect, useRef, useState } from 'react';
import { DocumentView } from '@my-helpers/common';

interface DocumentPreviewProps {
    currentDocument: DocumentView;
    getContent: (documentId: string) => Promise<any>;
}

export const DocumentPreview: FC<DocumentPreviewProps> = ({
    currentDocument,
    getContent
}) => {
    const [contentUrl, setContentUrl] = useState<string>('');
    const [textContent, setTextContent] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    // Use ref to track the current blob URL for proper cleanup
    const currentBlobUrlRef = useRef<string | null>(null);

    const { id, file_name, mime_type } = currentDocument;

    useEffect(() => {
        // Reset state immediately when document changes to prevent showing stale content
        setContentUrl('');
        setTextContent('');
        setIsLoading(true);
        
        // Cleanup any previous blob URL
        if (currentBlobUrlRef.current) {
            URL.revokeObjectURL(currentBlobUrlRef.current);
            currentBlobUrlRef.current = null;
        }

        const fetchContent = async () => {
            if (!id || !mime_type) return;

            try {
                const response = await getContent(id);
                console.log('Response type:', typeof response, 'Response:', response);

                // For text files, get the text content from the blob
                if (mime_type === 'text/plain') {
                    if (response instanceof Blob) {
                        const text = await response.text();
                        setTextContent(text);
                    } else {
                        const text = typeof response === 'string' ? response : String(response);
                        setTextContent(text);
                    }
                } else {
                    // For binary files (PDF, images), response should be a Blob
                    let blob: Blob;

                    if (response instanceof Blob) {
                        blob = response;
                    } else {
                        // Fallback for other response types
                        blob = new Blob([response], { type: mime_type });
                    }

                    const url = URL.createObjectURL(blob);
                    currentBlobUrlRef.current = url;
                    setContentUrl(url);
                }
                setIsLoading(false);
            } catch (err) {
                console.error('Error loading document content:', err);
                setIsLoading(false);
            }
        };

        fetchContent();

        // Cleanup blob URL when component unmounts or document changes
        return () => {
            if (currentBlobUrlRef.current) {
                URL.revokeObjectURL(currentBlobUrlRef.current);
                currentBlobUrlRef.current = null;
            }
        };
    }, [id, mime_type, getContent]);

    return (
        <Box sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            backgroundColor: 'background.paper',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column'
        }}>
            <Typography variant="h6" 
                my={1} 
                px={2} 
                sx={{ 
                    color: '#1f2937',
                    flexShrink: 0 // Prevent header from shrinking
                }}>
                Preview
            </Typography>
            <Box 
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    minHeight: 0, // Important for proper flex scrolling
                    display: 'flex',
                    padding: '0 10px 10px 10px',
                    // Center only for loading, PDFs, and images
                    alignItems: mime_type === 'text/plain' && !isLoading ? 'stretch' : 'center',
                    justifyContent: mime_type === 'text/plain' && !isLoading ? 'stretch' : 'center'
                }}>
            {isLoading && (
                <CircularProgress size={40} />
            )}
            {!isLoading && mime_type === 'application/pdf' && contentUrl && (
                <object
                    key={id} // Force remount when document changes
                    data={`${contentUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                    style={{ border: 'none', display: 'block' }}
                    aria-label={`PDF Preview - ${file_name}`}
                >
                    <Box sx={{ p: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            Unable to display PDF. Your browser may not support PDF preview.
                        </Typography>
                    </Box>
                </object>
            )}
            {!isLoading && (mime_type === 'image/png' || mime_type === 'image/jpg' || mime_type === 'image/jpeg') && contentUrl && (
                <Box sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }}>
                    <img
                        src={contentUrl}
                        alt={`Image Preview - ${file_name}`}
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain'
                        }}
                    />
                </Box>
            )}
            {!isLoading && mime_type === 'text/plain' && textContent && (
                <Box sx={{
                    width: '100%',
                    padding: 0,
                    height: '100%',
                    overflow: 'auto',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center'
                }}>
                    <Box sx={{
                        backgroundColor: 'white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
                        padding: 3,
                        margin: 2,
                        maxWidth: '100%',
                        minWidth: '80%',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        fontSize: '0.45rem',
                        lineHeight: 1.4,
                        color: '#1f2937',
                    }}>
                        {textContent}
                    </Box>
                </Box>
            )}
            {!isLoading && !['application/pdf', 'image/png', 'image/jpg', 'image/jpeg', 'text/plain'].includes(mime_type || '') && (
                <Box sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 2,
                    p: 3
                }}>
                    <Typography variant="h6">Preview not available</Typography>
                    <Typography variant="body2" color="text.secondary">
                        File type "{mime_type}" is not supported for preview
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        File: {file_name}
                    </Typography>
                </Box>
            )}
            </Box>
        </Box>
    );
};